import { Button, Carousel, message, Popover } from "antd";
import { useRef } from "react";
import { Target04 } from "@carespace-icons/general/target04";
import { useTranslation } from "react-i18next";
import { Calendar } from "@carespace-icons/time/calendar";
import { ChevronLeft } from "@carespace-icons/arrows/chevronLeft";
import { ChevronRight } from "@carespace-icons/arrows/chevronRight";
import { useNavigate } from "react-router-dom";
import { useTypedDispatch, useTypedSelector } from "@stores/index";
import { CarouselRef } from "antd/es/carousel";
import './ExistingPatientCarousel.css';
import { createProgram } from "@stores/aiAssistant/program";
import { ISuggestedProgramItem, TSuggestedProgram, TSuggestedProgrammExerciseItem } from "@stores/interfaces";

const ExistingPatientDashboard = (props: { suggestedProgramData: ISuggestedProgramItem[], fetchHomeData: (value: number) => void}) => {
  const dispatch = useTypedDispatch()
  const { suggestedProgramData, fetchHomeData } = props
  const { selectedUser } = useTypedSelector(state => state.contacts.main);
  const buttonStyle = {
    color: "var(--button-text-color)",
    border: 'inherit',
    width: '200px',
    height: "50px",
    marginTop: '6px',
    marginBottom: '6px'
  };
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useTypedSelector(state => state.user);
  const carouselRef = useRef<CarouselRef>(null);

const scrollLeft = () => {
  if (carouselRef.current) {
    carouselRef.current.prev();
  }
};

const scrollRight = () => {
  if (carouselRef.current) {
    carouselRef.current.next();
  }
};

const handleEnroll = async (item: TSuggestedProgram) => {
    const exercises: { strapiExerciseId: string; exerciseLibraryId: string; weeklyReps: number; dailyReps: number; sets: number; reps: number; order: number; }[] = [];
    item?.exercises?.forEach(
      (exercise: TSuggestedProgrammExerciseItem, index: number) => {
        const tempExercise = {
          strapiExerciseId: String(typeof exercise?.strapiExerciseId === 'object' ? exercise?.strapiExerciseId?.id : exercise?.strapiExerciseId || ''),
          exerciseLibraryId: exercise.exerciseLibraryId || '',
          weeklyReps: exercise.weeklyReps,
          dailyReps: exercise.dailyReps,
          sets: exercise.sets,
          reps: exercise.reps,
          order: index + 1,
        };
        exercises.push(tempExercise);
      },
    );
    const formData = new FormData();
    formData.append('name', item?.name);
    formData.append('duration', item?.duration.toString());
    formData.append('durationType', item?.durationType);
    formData.append('description', item?.description);
    formData.append('thumbnail', typeof item?.thumbnail === 'string' ? item?.thumbnail : item?.thumbnail?.url || '');
    if (user.isPhysioterapist ? selectedUser?.id : user?.id) {
      formData.append('userId', String(user.isPhysioterapist ? selectedUser?.id : user?.id));
    } else {
      console.error('userId is missing or invalid');
      return;
    }
    formData.append('strapiProgramTemplateId', item?.id);
    exercises?.forEach((exercise, index) => {
      Object.keys(exercise).forEach(key => {
        if (exercise[key as keyof typeof exercise]) {
          formData.append(
            `exercises[create][${index}][${key}]`,
            exercise[key as keyof typeof exercise] as string,
          );
        }
      });
    });
    const data = await dispatch(createProgram(formData));
    if (data?.payload?.id) {
      message.destroy();
      message.success(t('Patient.data.newDashboard.enrolled'));
      fetchHomeData(1);
    }
  }

return (
  <div className="existing-patient-carousel-container">
    <Carousel ref={carouselRef} autoplay>
      {suggestedProgramData?.map((item) => (
        <div
          key={item.id}
          className="existing-patient-carousel-item"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/${user.isPhysioterapist ? selectedUser?.id : user?.id}/overview`, {
              state: { selectedProgram: item }
            });
          }}
        >
          <div className="existing-patient-image-wrapper">
            <div className="existing-patient-image-overlay"></div>
            <img
              src={item?.thumbnail?.url || item?.exercises?.[0]?.strapiExerciseId?.exercise_image?.[0]?.url}
              alt={item?.name}
              className={`existing-patient-image ${item?.thumbnail ? "existing-patient-object-cover" : "existing-patient-object-contain"}`}
            />
          </div>
          <div className="existing-patient-info">
            <div className="existing-patient-duration">
              <p className="existing-patient-duration-text">
                <Calendar /> {item?.duration}{" "}
                {item?.durationType
                  ? item.duration > 1
                    ? item.durationType.charAt(0).toUpperCase() + item.durationType.slice(1)
                    : item.durationType.charAt(0).toUpperCase() + item.durationType.slice(1).replace(/s$/, "")
                  : ""}
              </p>
            </div>
            <div className="existing-patient-title">
              <p className="existing-patient-program-name">{item?.name}</p>
              <div className="existing-patient-functional-goals">
                {item?.functional_goals?.[0] && (
                  <p className="existing-patient-goal-tag">{item.functional_goals[0].name}</p>
                )}
                {item?.functional_goals?.[1] && (
                  <p className="existing-patient-goal-tag">{item.functional_goals[1].name}</p>
                )}
                {item?.functional_goals?.length! > 2 && (
                  <Popover
                    content={
                      <div className="existing-patient-popover-content">
                        <div className="existing-patient-popover-header">
                          <h3 className="existing-patient-popover-title">
                            <Target04 width={17} height={17} color="stroke-gray-700" /> {t('Patient.data.newDashboard.functionalGoals')}
                          </h3>
                        </div>
                        <hr className="existing-patient-popover-divider" />
                        {item?.functional_goals?.map((goal) => (
                          <li key={goal.name} className="existing-patient-goal-tag">
                            {goal.name}
                          </li>
                        ))}
                      </div>
                    }
                    trigger="hover"
                    placement="topLeft"
                  >
                    <p
                      className="existing-patient-show-all-tag"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('Patient.data.newDashboard.showAll')}
                    </p>
                  </Popover>
                )}
              </div>
            </div>
            <Popover
              content={item.description}
              trigger="hover"
              placement="topLeft"
              overlayInnerStyle={{
                maxWidth: "50vw",
                whiteSpace: "normal"
              }}
              overlayStyle={{
                overflowX: "hidden"
              }}
              autoAdjustOverflow
            >
              <p className="existing-patient-description">{item?.description}</p>
            </Popover>
            <Button
              className="start-session-css"
              style={buttonStyle}
              onClick={(e) => {
                e.stopPropagation();
                handleEnroll(item as TSuggestedProgram);
              }}
            >
              <span className="existing-patient-enroll-button-text">{t('Patient.data.newDashboard.enroll')}</span>
            </Button>
          </div>
        </div>
      ))}
    </Carousel>
    <span className="existing-patient-carousel-left-arrow" onClick={scrollLeft}>
      <ChevronLeft width={30} height={30} />
    </span>
    <span className="existing-patient-carousel-right-arrow" onClick={scrollRight}>
      <ChevronRight width={30} height={30} />
    </span>
  </div>
);
};

export default ExistingPatientDashboard;
